import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { ArrowRight, Building2, Copy, GitMerge, Home, Users } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import type { TransferFragment } from "../types"
import type { TransferProcessEntityHeaderProps } from "../types"

const ACTION_LABELS: Record<TransferFragment, string> = {
  transfer_company: "Transfer company",
  transfer_property: "Transfer property",
  merge_property: "Merge property",
  transfer_resident: "Transfer residents",
  split_property: "Split property",
}

const FRAGMENT_ICONS: Record<TransferFragment, LucideIcon> = {
  transfer_company: Building2,
  transfer_property: Home,
  merge_property: GitMerge,
  transfer_resident: Users,
  split_property: Copy,
}

type ChipVariant = "source" | "target" | "accent"

type ValueChipProps = {
  children: ReactNode
  variant?: ChipVariant
  testId?: string
}

function ValueChip({ children, variant = "source", testId }: ValueChipProps) {
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex max-w-[10rem] items-center rounded-md border px-2 py-0.5 text-xs font-medium sm:max-w-[12rem]",
        variant === "source" && "border-slate-200/90 bg-white text-[#253c5c]",
        variant === "target" && "border-[#1597ff]/25 bg-[#1597ff]/[0.07] font-semibold text-[#1597ff]",
        variant === "accent" && "border-emerald-200/80 bg-emerald-50 font-semibold text-emerald-700",
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  )
}

type TransferFlowRowProps = {
  label: string
  from?: string
  to?: string
  testId?: string
  fromTestId?: string
  toTestId?: string
  showArrow?: boolean
  singleValue?: boolean
}

function TransferFlowRow({
  label,
  from,
  to,
  testId,
  fromTestId,
  toTestId,
  showArrow = true,
  singleValue = false,
}: TransferFlowRowProps) {
  const fromValue = from?.trim() || "—"
  const toValue = to?.trim() || "—"
  const targetVariant = toValue === "—" ? "source" : "target"

  return (
    <div data-testid={testId} className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {singleValue ? (
        <ValueChip variant="accent" testId={fromTestId ?? toTestId}>
          {fromValue}
        </ValueChip>
      ) : (
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <ValueChip variant="source" testId={fromTestId}>
            {fromValue}
          </ValueChip>
          {showArrow ? (
            <>
              <ArrowRight
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-[#1597ff]"
                strokeWidth={2.5}
              />
              <ValueChip variant={targetVariant} testId={toTestId}>
                {toValue}
              </ValueChip>
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

function TransferFlowStrip({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-4 sm:gap-y-2 sm:border-t-0 sm:pt-0 lg:gap-x-5"
    >
      {children}
    </div>
  )
}



/** Primary entity summary above the stepper (property/company name + transfer arrows). */
export function TransferProcessEntityHeader({ ctx }: TransferProcessEntityHeaderProps) {
  const {
    fragment,
    propertyData,
    getValues,
    loadNewCompany,
    newPropertyLabel,
    newPropertyName,
  } = ctx

  if (!fragment) return null

  const Icon = FRAGMENT_ICONS[fragment]

  const primaryName =
    fragment === "transfer_company"
      ? String(propertyData?.company_name ?? "")
      : fragment === "split_property"
        ? String(getValues("property_name") || "")
        : String(propertyData?.property_name ?? "")

  const primaryNameTestId =
    fragment === "split_property"
      ? "transfer-process-entity-primary-name-split"
      : "transfer-process-entity-primary-name"

  const hasFlowStrip =
    ["transfer_property", "transfer_company", "merge_property", "transfer_resident", "split_property"].includes(
      fragment,
    )

  return (
    <div
      data-testid="transfer-process-entity-header"
      className="mb-4 rounded-xl border border-slate-200/90 bg-gradient-to-r from-sky-50/60 via-white to-white px-3.5 py-3 shadow-sm ring-1 ring-slate-100 sm:px-4"
    >
      <div
        className={cn(
          "flex flex-col gap-3",
          hasFlowStrip && "lg:flex-row lg:items-center lg:justify-between lg:gap-5",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#1597ff] to-[#0d7acc] text-white shadow-sm shadow-[#1597ff]/25"
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
          </div>

          <div className="min-w-0">
            <span className="inline-flex rounded-full bg-[#1597ff]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#1597ff]">
              {ACTION_LABELS[fragment]}
            </span>
            <h2
              data-testid="transfer-process-entity-title"
              className="mt-0.5 truncate font-['Roboto',sans-serif] text-base font-bold tracking-tight text-[#0f172a] sm:text-lg"
            >
              <span data-testid={primaryNameTestId}>{primaryName}</span>
            </h2>
          </div>
        </div>

        {["transfer_property", "transfer_company"].includes(fragment) && (
          <TransferFlowStrip testId="transfer-process-company-domain-summary">
            <TransferFlowRow
              label="Company"
              from={String(getValues("company_name") || "")}
              to={String(loadNewCompany?.company_name ?? "")}
              testId="transfer-process-company-domain-summary-company-line"
            />
          </TransferFlowStrip>
        )}

        {fragment === "merge_property" && (
          <TransferFlowStrip testId="transfer-process-merge-property-summary">
            <TransferFlowRow
              label="Property"
              from={String(propertyData?.property_name ?? "")}
              to={newPropertyLabel}
              testId="transfer-process-merge-property-summary-property-line"
            />
          </TransferFlowStrip>
        )}

        {fragment === "transfer_resident" && (
          <TransferFlowStrip testId="transfer-process-transfer-resident-summary">
            <TransferFlowRow
              label="Residents"
              from={String(propertyData?.property_name ?? "")}
              to={newPropertyLabel}
              fromTestId="transfer-process-transfer-resident-summary-from"
              toTestId="transfer-process-transfer-resident-summary-to"
            />
          </TransferFlowStrip>
        )}

        {fragment === "split_property" && (
          <TransferFlowStrip testId="transfer-process-split-property-summary">
            <TransferFlowRow
              label="Cloned property"
              from={String(newPropertyName || "")}
              toTestId="transfer-process-split-property-cloned-name"
              singleValue
            />
          </TransferFlowStrip>
        )}
      </div>
    </div>
  )
}
